import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Badge, Spinner, Image, SimpleGrid, Textarea } from "@chakra-ui/react";
import { ArrowLeft, MapPin, User, CalendarDays, Lock, FileDown, CheckCircle2, History, ShieldAlert, MessageCircle, Link2, Camera, HardHat } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { findActionTemplate, cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { uploadThumb, thumbPath } from "@/lib/imageThumbs";
import { resolveSubmission, displayStatus } from "@/lib/urgent";
import { useAuth } from "@/context/AuthContext";
import { PhotoAnswer, AnswersSections } from "@/components/submissions/AnswersView";
import { QuickPill, InlineCommentBox } from "@/components/QuickActions";
import { showToast } from "@/components/AppToaster";
import { statusMeta } from "@/lib/submissionMeta";
import { SignerList, signedCounts, AcknowledgeModal } from "@/components/AcknowledgeModal";
import { colors } from "@/theme/colors";

const API = process.env.REACT_APP_BACKEND_URL;

// Contractor display helpers — avoid recomputed joins inside JSX.
const contractorLabel = (sub) =>
  [sub.contractor_name, sub.contractor_company !== sub.contractor_name ? sub.contractor_company : null]
    .filter(Boolean).join(" — ");

const signoffSubtitle = (s) =>
  [s.contractor_company !== s.contractor_name ? s.contractor_company : null, s.contact]
    .filter(Boolean).join(" · ");
const fmt = (d) => new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const MetaItem = ({ icon, label, value }) => (
  <HStack gap="2" align="flex-start">
    <Icon as={icon} boxSize="4" color={colors.teal} mt="0.5" />
    <Box>
      <Text fontSize="xs" color={colors.textSecondary}>{label}</Text>
      <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{value}</Text>
    </Box>
  </HStack>
);

function DetailHeader({ sub, meta, reviewPill, onPdf, pdfBusy, onResolve, resolving, offline, canResolve }) {
  return (
    <Flex justify="space-between" align="flex-start" gap="4" flexWrap="wrap" mb="5" data-testid="detail-header">
      <Box>
        <HStack gap="2" flexWrap="wrap">
          <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "xl", md: "2xl" }} color={colors.textPrimary}>{sub.template_title}</Heading>
          <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5" data-testid="detail-status-badge">{meta.label}</Badge>
          {sub.revision > 1 && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2">Rev {sub.revision}</Badge>}
          {sub.source === "contractor" && <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2.5" data-testid="detail-contractor-badge">QR Contractor Portal</Badge>}
        </HStack>
        <Text fontSize="sm" color={colors.textSecondary} mt="1">{sub.template_type} · Template v{sub.template_version}</Text>
      </Box>
      <HStack gap="2" flexWrap="wrap">
        {reviewPill(null)}
        <Button onClick={onPdf} loading={pdfBusy} variant="outline" data-testid="export-pdf-btn">
          <FileDown size={16} /> Export PDF
        </Button>
        {canResolve && (
          <Button onClick={onResolve} loading={resolving} disabled={offline} bg={colors.sageDeep} _hover={{ bg: colors.sage }} data-testid="resolve-btn"
            title={offline ? "You need a connection to change status" : undefined}>
            <CheckCircle2 size={16} /> Mark as Resolved
          </Button>
        )}
      </HStack>
    </Flex>
  );
}

function DetailMeta({ sub, isIncident }) {
  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-meta">
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap="4">
        <MetaItem icon={MapPin} label="Location" value={sub.location_name} />
        {sub.source === "contractor" ? (
          <MetaItem icon={HardHat} label="Contractor" value={contractorLabel(sub)} />
        ) : (
          <MetaItem icon={User} label="Employee" value={sub.worker_name} />
        )}
        <MetaItem icon={CalendarDays} label="Submitted" value={fmt(sub.created_at)} />
        {sub.completed_at && <MetaItem icon={CheckCircle2} label="Completed in field" value={fmt(sub.completed_at)} />}
      </SimpleGrid>
      <HStack mt="4" gap="1.5" color={colors.textSecondary}>
        <Icon as={Lock} boxSize="3.5" />
        <Text fontSize="xs">
          {isIncident
            ? "Incident Reports are legal records — the content is permanently locked and cannot be edited."
            : "Submitted content is immutable — only the status can change, and every change is audited."}
        </Text>
      </HStack>
    </Box>
  );
}

function SignoffSection({ signoffs }) {
  const [urls, setUrls] = useState({});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        // Time-limited signed URLs — signatures are never publicly reachable.
        const results = await Promise.all(signoffs.map((s) =>
          supabase.storage.from("submission-photos").createSignedUrl(s.signature_path, 300)));
        const out = {};
        signoffs.forEach((s, i) => { if (results[i].data?.signedUrl) out[s.signature_path] = results[i].data.signedUrl; });
        setUrls(out);
        if (!Object.keys(out).length && signoffs.length) setFailed(true);
      } catch { setFailed(true); } // offline: show placeholder, not an eternal spinner
    })();
  }, [signoffs]);
  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-signoffs">
      <HStack gap="2" mb="3">
        <Icon as={HardHat} boxSize="4" color={colors.teal} />
        <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Contractor sign-offs</Text>
      </HStack>
      <Stack gap="3">
        {signoffs.map((s) => (
          <Flex key={s.id} align="center" gap="4" flexWrap="wrap" data-testid={`detail-signoff-${s.id}`}>
            {urls[s.signature_path] ? (
              <Image src={urls[s.signature_path]} alt={`${s.contractor_name} signature`} maxW="180px" maxH="70px"
                bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="10px" />
            ) : failed ? (
              <Flex w="180px" h="70px" align="center" justify="center" borderRadius="10px" bg={colors.cream}
                border="1px solid" borderColor={colors.border}>
                <Text fontSize="10px" color={colors.textSecondary}>Signature needs a connection</Text>
              </Flex>
            ) : (
              <Flex w="180px" h="70px" align="center" justify="center" borderRadius="10px" bg={colors.cream}>
                <Spinner size="sm" color={colors.teal} />
              </Flex>
            )}
            <Box>
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{s.contractor_name}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>{signoffSubtitle(s)}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>Signed {fmt(s.signed_at)}</Text>
            </Box>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}


const PhotoGroups = ({ photos, prefix, withAuthors = false, userId = null, onDeletePhoto = null, onReplacePhoto = null }) => (
  <Stack gap={withAuthors ? "3" : "2"}>
    {[...new Set(photos.map((p) => p.section_title || "Whole report"))].map((grp) => {
      const grpPhotos = photos.filter((p) => (p.section_title || "Whole report") === grp);
      return (
        <Box key={grp}>
          <Text fontSize="xs" color={colors.textSecondary} mb="1.5">
            {grp}{withAuthors ? ` · ${[...new Set(grpPhotos.map((p) => p.author_name))].join(", ")}` : ""}
          </Text>
          {onDeletePhoto ? (
            <Flex gap="3" flexWrap="wrap">
              {grpPhotos.map((p) => (
                <Box key={p.id}>
                  <PhotoAnswer photos={[p]} fieldId={`${prefix}-${p.id}`} />
                  {p.author === userId && (
                    <HStack gap="1" mt="1">
                      <Button size="xs" variant="ghost" color={colors.criticalText}
                        onClick={() => onDeletePhoto(p)} data-testid={`review-photo-delete-${p.id}`}>
                        Delete
                      </Button>
                      <Button size="xs" variant="ghost" as="label" cursor="pointer" data-testid={`review-photo-replace-${p.id}`}>
                        Replace
                        <input type="file" accept="image/*" hidden
                          onChange={(e) => e.target.files?.[0] && onReplacePhoto(p, e.target.files[0])}
                          data-testid={`review-photo-replace-input-${p.id}`} />
                      </Button>
                    </HStack>
                  )}
                </Box>
              ))}
            </Flex>
          ) : (
            <PhotoAnswer photos={grpPhotos} fieldId={`${prefix}-${grp}`} />
          )}
        </Box>
      );
    })}
  </Stack>
);

const LinkedActionsCard = ({ actions, navigate }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-linked-actions">
    <HStack gap="2" mb="3">
      <Icon as={Link2} boxSize="4" color={colors.poppy} />
      <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Actions raised on this report</Text>
    </HStack>
    <Stack gap="2">
      {actions.map((k) => {
        const km = statusMeta(displayStatus(k));
        return (
          <Flex key={k.id} as="button" type="button" onClick={() => navigate(`/submissions/${k.id}`)}
            data-testid={`linked-action-${k.id}`} w="full" textAlign="left" align="center" gap="3"
            px="3.5" py="2.5" borderRadius="10px" border="1px solid" borderColor={colors.border}
            _hover={{ borderColor: colors.teal, bg: colors.tealTint }}>
            <Box flex="1">
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{k.template_title}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>
                {k.link_context?.section_title ? `From: ${k.link_context.section_title} · ` : ""}{k.worker_name} · {fmt(k.created_at)}
              </Text>
            </Box>
            <Badge bg={km.bg} color={km.color} borderRadius="full" px="2.5">{km.label}</Badge>
          </Flex>
        );
      })}
    </Stack>
  </Box>
);

const CommentsCard = ({ comments, newComment, setNewComment, postComment, posting, offline, userId, onEditComment }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  return (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-comments">
    <HStack gap="2" mb="3">
      <Icon as={MessageCircle} boxSize="4" color={colors.sageDeep} />
      <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Comments ({comments.length})</Text>
    </HStack>
    <Stack gap="3">
      {comments.length === 0 && <Text fontSize="sm" color={colors.textSecondary}>No comments yet.</Text>}
      {comments.map((c) => (
        <Box key={c.id} px="3.5" py="2.5" borderRadius="10px" bg={colors.page} data-testid={`comment-${c.id}`}>
          <HStack gap="2" mb="0.5" flexWrap="wrap">
            <Text fontSize="xs" fontWeight="700" color={colors.textPrimary}>{c.author_name || "Unknown"}</Text>
            <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px" textTransform="none">
              {c.section_title || "Whole report"}
            </Badge>
            <Text fontSize="xs" color={colors.textSecondary}>{fmt(c.created_at)}</Text>
            {c.edited_at && (
              <Text fontSize="xs" color={colors.textSecondary} fontStyle="italic" data-testid={`comment-edited-tag-${c.id}`}>
                edited {fmt(c.edited_at)}
              </Text>
            )}
            {c.author === userId && !offline && editingId !== c.id && (
              <Button size="xs" variant="ghost" color={colors.tealHover} h="5" px="1.5"
                onClick={() => { setEditingId(c.id); setDraft(c.body); }} data-testid={`comment-edit-btn-${c.id}`}>
                Edit
              </Button>
            )}
          </HStack>
          {editingId === c.id ? (
            <Box>
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} fontSize="sm"
                bg={colors.white} borderColor={colors.border} data-testid={`comment-edit-input-${c.id}`} />
              <HStack gap="2" mt="1.5">
                <Button size="xs" loading={saving} disabled={!draft.trim() || draft.trim() === c.body}
                  onClick={async () => { setSaving(true); await onEditComment(c, draft.trim()); setSaving(false); setEditingId(null); }}
                  data-testid={`comment-edit-save-${c.id}`}>
                  Save edit
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setEditingId(null)} data-testid={`comment-edit-cancel-${c.id}`}>
                  Cancel
                </Button>
              </HStack>
            </Box>
          ) : (
            <Text fontSize="sm" color={colors.textPrimary} whiteSpace="pre-wrap">{c.body}</Text>
          )}
        </Box>
      ))}
      <Box>
        <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={2}
          placeholder="Add a comment — it becomes a permanent part of this record." fontSize="sm"
          bg={colors.white} borderColor={colors.border} data-testid="detail-comment-input" />
        <Button mt="2" size="sm"
          onClick={postComment} loading={posting} disabled={!newComment.trim() || offline} data-testid="detail-comment-save"
          title={offline ? "You need a connection to comment" : undefined}>
          Add comment
        </Button>
        {offline && <Text mt="1" fontSize="xs" color={colors.textSecondary}>Comments and status changes need a connection.</Text>}
      </Box>
    </Stack>
  </Box>
  );
};

const RequiredSignaturesCard = ({ assignment, mySignoff, onSign }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-required-signatures">
    <Flex justify="space-between" align="center" mb="3" gap="2" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Required signatures</Text>
      <HStack gap="2">
        <Badge bg={assignment.status === "completed" ? "#D7F0EC" : "#FBEEDA"}
          color={assignment.status === "completed" ? "#0F6E56" : "#8A5106"} borderRadius="full" px="2.5"
          data-testid="detail-signatures-progress">
          {assignment.status === "completed"
            ? "All signatures collected"
            : `Awaiting signatures (${signedCounts(assignment).signed} of ${signedCounts(assignment).total} signed)`}
        </Badge>
        {mySignoff && (
          <Button size="sm" onClick={onSign} data-testid="detail-sign-btn">Sign</Button>
        )}
      </HStack>
    </Flex>
    {mySignoff && (
      <Text fontSize="xs" color="#0F6E56" mb="2" data-testid="detail-sign-hint">
        Your signature is pending — you can review this form and sign right here.
      </Text>
    )}
    <SignerList signoffs={[...assignment.assignment_signoffs].sort((x, y) => (x.signer_role === "assignee" ? -1 : 1) - (y.signer_role === "assignee" ? -1 : 1))} showSignatures />
  </Box>
);

// Signature lifecycle + review-content events surfaced alongside status changes.
// Every audited action a reviewer can take on this record shows here (user-approved whitelist).
const SIGN_EVENT_LABELS = {
  signature_added: { label: "Signature added", bg: colors.white },
  assignment_awaiting_signatures: { label: "Awaiting signatures", bg: colors.white },
  assignment_completed: { label: "All signatures collected", bg: colors.tealTint, color: colors.tealHover },
  contractor_signoff: { label: "Contractor signed", bg: colors.white },
  document_acknowledged: { label: "Document acknowledged", bg: colors.white },
  photo_added: { label: "Review photo added", bg: colors.white },
  photo_removed: { label: "Review photo removed", bg: colors.white },
  comment_added: { label: "Comment added", bg: colors.white },
  comment_edited: { label: "Comment edited", bg: colors.white },
};

const AuditCard = ({ sub, statusChanges }) => (
  <Box bg={colors.cream} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid="detail-audit">
    <HStack gap="2" mb="3">
      <Icon as={History} boxSize="4" color={colors.sageDeep} />
      <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Status & audit history</Text>
    </HStack>
    <Stack gap="2">
      <HStack gap="2" flexWrap="wrap">
        <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5" textTransform="none">Submitted</Badge>
        <Text fontSize="sm" color={colors.textPrimary}>by <b>{sub.worker_name}</b></Text>
        <Text fontSize="xs" color={colors.textSecondary}>{fmt(sub.created_at)}</Text>
      </HStack>
      {statusChanges.map((a) => {
        const ev = SIGN_EVENT_LABELS[a.action];
        const who = a.details?.signer || a.details?.author_name || a.details?.worker_name || a.actor_name;
        return (
          <HStack key={a.id} gap="2" flexWrap="wrap" data-testid={`audit-row-${a.id}`}>
            <Badge bg={ev?.bg || colors.white} color={ev?.color || colors.textPrimary} borderRadius="full" px="2.5" textTransform="none">
              {ev ? ev.label : `${statusMeta(a.details?.from).label} → ${statusMeta(a.details?.to).label}`}
            </Badge>
            {who && (
              <Text fontSize="sm" color={colors.textPrimary}>by <b>{who}</b></Text>
            )}
            <Text fontSize="xs" color={colors.textSecondary}>{fmt(a.created_at)}</Text>
          </HStack>
        );
      })}
      {statusChanges.length === 0 && (
        <Text fontSize="sm" color={colors.textSecondary}>No status changes yet.</Text>
      )}
    </Stack>
  </Box>
);

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile, activeCompanyId, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [audit, setAudit] = useState([]);
  const [comments, setComments] = useState([]);
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [signoffs, setSignoffs] = useState([]);
  const [assignment, setAssignment] = useState(null);
  const [openQC, setOpenQC] = useState(null);
  const [children, setChildren] = useState([]);
  const [parent, setParent] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [offline, setOffline] = useState(false);
  const [myWorkerId, setMyWorkerId] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    const [sRes, aRes, cmRes, kidsRes, rpRes, soRes, faRes] = await safeAll([
      supabase.from("submissions").select("*").eq("id", id).maybeSingle(),
      supabase.rpc("submission_audit", { sub: id }),
      supabase.from("submission_comments").select("*").eq("submission_id", id).order("created_at"),
      supabase.from("submissions").select("id,template_title,template_type,status,flagged,worker_name,created_at,link_context")
        .eq("parent_submission_id", id).order("created_at"),
      supabase.from("submission_review_photos").select("*").eq("submission_id", id).order("created_at"),
      supabase.from("contractor_signoffs").select("*").eq("submission_id", id).order("created_at"),
      supabase.from("form_assignments").select("*, assignment_signoffs(*)").eq("submission_id", id).limit(1),
    ]);
    if (sRes.error) {
      // Offline / severed fetch: serve the last-known-good copy of this record.
      const snap = cacheGet(user.id, `subdetail:${id}`);
      if (snap?.sub) {
        setSub(snap.sub); setAudit(snap.audit || []); setComments(snap.comments || []);
        setChildren(snap.children || []); setReviewPhotos(snap.reviewPhotos || []);
        setSignoffs(snap.signoffs || []); setAssignment(snap.assignment || null);
        setParent(snap.parent || null);
        window.dispatchEvent(new CustomEvent("hakina:submission-viewed", { detail: { id } }));
      } else {
        setSub(null);
      }
      setOffline(true);
      setLoading(false);
      return;
    }
    const s = sRes.data;
    let p = null;
    if (s?.parent_submission_id) {
      const { data: pd } = await safeQuery(supabase.from("submissions").select("id,template_title").eq("id", s.parent_submission_id).maybeSingle());
      p = pd || null;
    }
    setSub(s || null);
    setAudit(aRes.data || []);
    setComments(cmRes.data || []);
    setChildren(kidsRes.data || []);
    setReviewPhotos(rpRes.data || []);
    setSignoffs(soRes.data || []);
    setAssignment(faRes.data?.[0] || null);
    setParent(p);
    setOffline(false);
    if (s) {
      cachePut(user.id, `subdetail:${id}`, {
        sub: s, audit: aRes.data || [], comments: cmRes.data || [], children: kidsRes.data || [],
        reviewPhotos: rpRes.data || [], signoffs: soRes.data || [], assignment: faRes.data?.[0] || null, parent: p,
      });
      // Tell the alerts bell this record was viewed — its badge decrements.
      window.dispatchEvent(new CustomEvent("hakina:submission-viewed", { detail: { id } }));
    }
    setLoading(false);
  }, [id, user.id]);
  useEffect(() => { load(); }, [load]);

  // Option B: resolve the viewer's worker row in this record's company so a
  // pending signer can sign directly from the Required signatures section.
  useEffect(() => {
    if (!sub?.company_id) { setMyWorkerId(null); return; }
    let live = true;
    safeQuery(supabase.from("workers").select("id").eq("profile_id", user.id).eq("company_id", sub.company_id).limit(1))
      .then(({ data }) => { if (live) setMyWorkerId(data?.[0]?.id || null); });
    return () => { live = false; };
  }, [sub?.company_id, user.id]);

  const mySignoff = useMemo(() => {
    if (!assignment || !myWorkerId || assignment.status === "superseded") return null;
    return (assignment.assignment_signoffs || []).find(
      (s) => s.status === "pending" && s.worker_id === myWorkerId
    ) || null;
  }, [assignment, myWorkerId]);

  const statusChangeRows = useMemo(
    () => audit.filter((a) => a.action === "status_change" || SIGN_EVENT_LABELS[a.action]),
    [audit]
  );

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const { error } = await supabase.from("submission_comments").insert({
      company_id: sub.company_id, submission_id: id, section_id: null, section_title: null,
      author: user.id, author_name: profile?.full_name || user.email, body: newComment.trim(),
    });
    if (error) setErr("Comments and status changes need a connection.");
    else setNewComment("");
    await load();
    setPosting(false);
  };

  // ---- Review Quick Actions (pill on the read-only report) ----
  const scopeId = (scope) => scope?.fieldId || scope?.sectionId || null;
  const scopeKey = (scope) => scopeId(scope) || "report";
  const qcToggle = (scope) => setOpenQC((k) => (k === scopeKey(scope) ? null : scopeKey(scope)));
  const qcSaveComment = async (scope, body) => {
    setErr(null);
    const { error } = await supabase.from("submission_comments").insert({
      company_id: sub.company_id, submission_id: id, section_id: scopeId(scope), section_title: scope?.sectionTitle || null,
      author: user.id, author_name: profile?.full_name || user.email, body,
    });
    if (error) { setErr(`Could not save comment: ${error.message}`); return; }
    setOpenQC(null);
    showToast("Comment Added");
    await load();
  };
  const qcAddPhotos = async (photos, scope) => {
    setErr(null);
    try {
      const rows = [];
      for (const p of photos) {
        const blob = await (await fetch(p.dataUrl)).blob();
        const safeName = (p.name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `${sub.company_id}/review-${crypto.randomUUID()}-${safeName}`;
        const { error } = await supabase.storage.from("submission-photos")
          .upload(path, blob, { contentType: p.type || "image/jpeg" });
        if (error) throw error;
        await uploadThumb(supabase, path, blob);
        rows.push({
          company_id: sub.company_id, submission_id: id, section_id: scopeId(scope),
          section_title: scope?.sectionTitle || null, author: user.id,
          author_name: profile?.full_name || user.email, path, name: p.name,
        });
      }
      const { error: ie } = await supabase.from("submission_review_photos").insert(rows);
      if (ie) throw ie;
      showToast(photos.length > 1 ? `${photos.length} Photos Added Successfully` : "Photo Added Successfully");
      await load();
    } catch (e) {
      setErr(`Could not attach photo: ${e.message}`);
    }
  };
  // ---- Reviewer-side controls (0014): edit own comments, delete/replace own review photos — all audited by DB triggers.
  const editComment = async (c, body) => {
    setErr(null);
    const { error } = await supabase.from("submission_comments").update({ body }).eq("id", c.id);
    if (error) { setErr(`Could not edit comment: ${error.message}`); return; }
    showToast("Comment Updated");
    await load();
  };
  const deleteReviewPhoto = async (p) => {
    setErr(null);
    const { error } = await supabase.from("submission_review_photos").delete().eq("id", p.id);
    if (error) { setErr(`Could not delete photo: ${error.message}`); return; }
    await supabase.storage.from("submission-photos").remove([p.path, thumbPath(p.path)]);
    showToast("Photo Removed");
    await load();
  };
  const replaceReviewPhoto = async (p, file) => {
    setErr(null);
    try {
      const safeName = (file.name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${sub.company_id}/review-${crypto.randomUUID()}-${safeName}`;
      const { error } = await supabase.storage.from("submission-photos")
        .upload(path, file, { contentType: file.type || "image/jpeg" });
      if (error) throw error;
      await uploadThumb(supabase, path, file);
      const { error: ie } = await supabase.from("submission_review_photos").insert({
        company_id: sub.company_id, submission_id: id, section_id: p.section_id,
        section_title: p.section_title, author: user.id,
        author_name: profile?.full_name || user.email, path, name: file.name,
      });
      if (ie) throw ie;
      const { error: de } = await supabase.from("submission_review_photos").delete().eq("id", p.id);
      if (de) throw de;
      await supabase.storage.from("submission-photos").remove([p.path, thumbPath(p.path)]);
      showToast("Photo Replaced");
      await load();
    } catch (e) {
      setErr(`Could not replace photo: ${e.message}`);
    }
  };

  const qcRaise = async (type, scope) => {
    const t = await findActionTemplate(supabase, user.id, type, sub.company_id);
    if (!t) { showToast(`The ${type} form isn't available on this device yet — open it once while online.`); return; }
    const params = new URLSearchParams({
      parent: id, ptitle: sub.template_title,
      psec: scope?.sectionTitle || "Whole report", psid: scopeId(scope) || "",
      ploc: sub.location_id || "",
    });
    navigate(`/forms/fill/${t.id}?${params.toString()}`);
  };
  const reviewPill = (scope) => (
    <QuickPill scope={scope} counts={{
      comments: comments.filter((c) => (c.section_id || null) === scopeId(scope)).length,
      photos: reviewPhotos.filter((p) => (p.section_id || null) === scopeId(scope)).length,
    }} commentOpen={openQC === scopeKey(scope)} onToggleComment={qcToggle} onAddPhotos={qcAddPhotos} onRaise={qcRaise} />
  );
  const qcBoxIfOpen = (scope) => openQC === scopeKey(scope) && (
    <InlineCommentBox scopeKey={scopeKey(scope)} onSave={(body) => qcSaveComment(scope, body)} onClose={() => setOpenQC(null)} />
  );

  const resolve = async () => {
    setErr(null);
    setResolving(true);
    // navigator.onLine may inform, never gate: the button is already disabled
    // by the reliable failed-fetch `offline` state; here we just attempt and
    // let a real failure surface the honest message.
    const { error } = await resolveSubmission(supabase, id);
    if (error) setErr("Comments and status changes need a connection.");
    await load();
    setResolving(false);
  };

  const downloadPdf = async () => {
    setErr(null);
    setPdfBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const fetchPdf = () => fetch(`${API}/api/submissions/${id}/pdf`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      let res = await fetchPdf();
      if (!res.ok && res.status !== 403) {
        // Transient outage (e.g. backend restarting during a deploy) — retry once.
        await new Promise((r) => setTimeout(r, 2500));
        res = await fetchPdf();
      }
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({})))?.detail;
        const hint = res.status === 404 || res.status >= 500
          ? " The server may be restarting — please try again in a minute."
          : "";
        throw new Error((detail || `PDF export failed (${res.status})`) + hint);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sub.template_title.replace(/[^a-zA-Z0-9 _-]/g, "")} - ${sub.worker_name} - ${new Date(sub.created_at).toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message);
    }
    setPdfBusy(false);
  };

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  if (!sub) {
    return (
      <Box data-testid="submission-notfound">
        <Text color={colors.textSecondary}>
          {offline
            ? "You're offline and this record hasn't been saved on this device yet — it will open once you're back online."
            : "This submission isn't available."}
        </Text>
        <Button mt="4" size="sm" variant="outline" onClick={() => navigate("/submissions")}>
          <ArrowLeft size={15} /> Back to submissions
        </Button>
      </Box>
    );
  }

  // Acting-company boundary: RLS may let a multi-membership user read this
  // record, but it must never render outside the company they're acting in.
  // DELIBERATE exception: Super-Admins are the platform operators and may open
  // any company's record — explicit by decision, not a null fallthrough.
  if (!isSuperAdmin && sub.company_id && activeCompanyId && sub.company_id !== activeCompanyId) {
    return (
      <Box data-testid="submission-wrong-company">
        <Text fontWeight="600" color={colors.textPrimary}>This record belongs to another of your companies.</Text>
        <Text mt="1" fontSize="sm" color={colors.textSecondary}>
          You're currently working in a different company, so it can't be shown here.
        </Text>
        <Button mt="4" size="sm" variant="outline" onClick={() => navigate("/submissions")}>
          <ArrowLeft size={15} /> Back to submissions
        </Button>
      </Box>
    );
  }

  const meta = statusMeta(displayStatus(sub));
  const sections = sub.template_schema?.sections || [];
  const isIncident = sub.template_type === "Incident Report";
  const statusChanges = statusChangeRows;

  return (
    <Box data-testid="submission-detail-page" maxW="860px">
      <Button size="xs" variant="ghost" mb="3" onClick={() => navigate("/submissions")} data-testid="back-to-submissions">
        <ArrowLeft size={14} /> All submissions
      </Button>

      <DetailHeader sub={sub} meta={meta} reviewPill={reviewPill} canResolve={displayStatus(sub) === "pending"}
        onPdf={downloadPdf} pdfBusy={pdfBusy} onResolve={resolve} resolving={resolving} offline={offline} />

      {offline && (
        <Box mb="4" px="4" py="3" borderRadius="10px" bg={colors.pendingBg} color={colors.pendingText} fontSize="sm" data-testid="detail-offline-banner">
          You're offline — showing the last saved copy of this record. Comments and status changes need a connection.
        </Box>
      )}

      {err && (
        <Box mb="4" px="4" py="3" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="detail-error">{err}</Box>
      )}

      {qcBoxIfOpen(null) && <Box mb="4">{qcBoxIfOpen(null)}</Box>}

      <DetailMeta sub={sub} isIncident={isIncident} />

      {/* Contractor sign-offs */}
      {signoffs.length > 0 && <SignoffSection signoffs={signoffs} />}

      {/* Raised-from link */}
      {sub.parent_submission_id && (
        <Flex as="button" type="button" onClick={() => parent && navigate(`/submissions/${sub.parent_submission_id}`)}
          data-testid="detail-parent-link" w="full" textAlign="left" align="center" gap="2" mb="4"
          px="4" py="3" borderRadius="12px" bg={colors.tealTint} border="1px solid" borderColor={colors.teal}>
          <Icon as={Link2} boxSize="4" color={colors.tealHover} />
          <Text fontSize="sm" color={colors.tealHover}>
            Raised from <b>{parent?.template_title || sub.link_context?.parent_title || "another report"}</b>
            {sub.link_context?.section_title ? ` — ${sub.link_context.section_title}` : ""}
            {!parent && " (report not yet synced)"}
          </Text>
        </Flex>
      )}

      {/* Flags */}
      {sub.flagged && (sub.flag_reasons || []).length > 0 && (
        <Box bg={colors.criticalBg} border="1px solid" borderColor="#F2C4C4" borderRadius="14px" p="4" mb="4" data-testid="detail-flags">
          <HStack gap="2" mb="2">
            <Icon as={ShieldAlert} boxSize="4" color={colors.poppy} />
            <Text fontSize="sm" fontWeight="700" color={colors.criticalText}>Auto-flagged answers</Text>
          </HStack>
          <Flex gap="2" flexWrap="wrap">
            {sub.flag_reasons.map((r, i) => (
              <Badge key={`${r}-${i}`} bg={colors.poppyTint} color={colors.poppy} borderRadius="full" px="2.5" textTransform="none" fontWeight="600">{r}</Badge>
            ))}
          </Flex>
        </Box>
      )}

      {/* Answers — rendered from the template snapshot saved with this submission */}
      <AnswersSections sections={sections} sub={sub} reviewPill={reviewPill} qcBoxIfOpen={qcBoxIfOpen} />

      {/* Quick-action photos */}
      {Array.isArray(sub.answers?._qa_photos) && sub.answers._qa_photos.length > 0 && (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-qa-photos">
          <HStack gap="2" mb="3">
            <Icon as={Camera} boxSize="4" color={colors.teal} />
            <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Quick photos</Text>
          </HStack>
          <PhotoGroups photos={sub.answers._qa_photos} prefix="qa" />
        </Box>
      )}

      {/* Review photos added by supervisors/admins */}
      {reviewPhotos.length > 0 && (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="detail-review-photos">
          <HStack gap="2" mb="3">
            <Icon as={Camera} boxSize="4" color={colors.sageDeep} />
            <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Review photos</Text>
          </HStack>
          <PhotoGroups photos={reviewPhotos} prefix="review" withAuthors userId={user.id}
            onDeletePhoto={offline ? null : deleteReviewPhoto} onReplacePhoto={offline ? null : replaceReviewPhoto} />
        </Box>
      )}

      {/* Actions raised on this report */}
      {children.length > 0 && <LinkedActionsCard actions={children} navigate={navigate} />}

      {/* Comments — immutable, company-only, audited */}
      <CommentsCard comments={comments} newComment={newComment} setNewComment={setNewComment}
        postComment={postComment} posting={posting} offline={offline} userId={user.id} onEditComment={editComment} />

      {/* Required signatures (scheduled assignment with additional signers) */}
      {assignment && (assignment.assignment_signoffs || []).length > 0 && (
        <RequiredSignaturesCard assignment={assignment} mySignoff={mySignoff} onSign={() => setSigning(true)} />
      )}
      {signing && mySignoff && (
        <AcknowledgeModal signoff={mySignoff} assignment={assignment}
          onClose={() => setSigning(false)} onDone={() => { setSigning(false); load(); }} />
      )}

      {/* Audit history */}
      <AuditCard sub={sub} statusChanges={statusChanges} />
    </Box>
  );
}
